import React, { Component } from 'react';
import { findDOMNode } from 'react-dom';
import { Player, BigPlayButton, ControlBar, Shortcut, VolumeMenuButton } from 'video-react';
import classnames from 'classnames';
import { canUserNotPermission, canUpdateGoogleDriveDeleteLogic } from '../authorizations';
import memoize from "memoize-one";

import AnimationMarkerForm from './AnimationMarkerForm';
import AnimationMarkersInPlayer from './AnimationMarkersInPlayer';
import AnimationMarkerPointsDisplay from './AnimationMarkerPointsDisplay';
import GdriveFileDeletedModal from './GdriveFileDeletedModal'
import MarkShape from './MarkShape';
import PolyLineSvg from "./PolyLineSvg";
import { styles } from "../util/styleDrawComponent";
import {getStyleOfSize} from "../util/getStyleofSize";
import SettingButton from './SettingButton';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, NavLink } from 'reactstrap';
import { fetchHeightHeader } from '../shared';
import CommentDetail from './CommentDetail';
import AnimationMarkerLine from './AnimationMarkerLine';
import AnimationCommentsPane from './AnimationCommentsPane';
import AnimationMarkerPeriod from './AnimationMarkerPeriod';
import imgConvertingThumbnail from '/app/assets/images/thumbnail_temp_pdf_black.png';
import { COMMENT_BOX_OPENED_EVENT, createCommentBoxSyncId, announceCommentBoxOpened } from '../util/animation-marker';

const markersVisibilities = ['all', 'circle', 'none'];
const showSVGIndex = 1000;
const hideSVGIndex = 0;
const timestamp = new Date().getTime();

export default class AnimationPlayer extends Component {
  constructor() {
    super();
    this.state = {
      isMarking: false,
      startX: 0,
      startY: 0,
      currentX: 0,
      currentY: 0,
      currentWidth: 0,
      currentHeight: 0,
      currentShape: 'point',
      shouldShowForm: false,
      markersVisibility: 'all',
      currentTime: 0,
      canvasWidth: 0,
      canvasHeight: 0,
      zIndexSvg: hideSVGIndex,
      penConfirmModal: null,
      listItemSvg: [],
      currentItemSvg: null,
      settingDraw: {
        color: "black",
        lineWidth: 3,
      },
      isLargeScreen: true,
      nativeVideoWidth: 0,
      nativeVideoHeight: 0,
      currentCanvasWidth: 0,
      currentCanvasHeight: 0,
      isShowControlBar: false,
      isShowModalNotCompareVersion: false,
      totalDuration: 0,
      periodRange: null,
      showPeriodRange: [],
      loadingText: null,
      isLoading: false,
      shouldShowGdriveFileDeletedModal: false,
      commentNumber: null,
    };
    this.overlayComponent = this.overlay();
    this.videoRef = React.createRef();

    this.showSeekBar = this.showSeekBar.bind(this);
    this.hintSeekBar = this.hintSeekBar.bind(this);
    this.commentBoxSyncId = createCommentBoxSyncId('animation-player');

    // Restore styles after fullscreen exits.
    this._controlBarTopBeforeFullscreen = null;
    this._controlBarPositionBeforeFullscreen = null;
    this._isFullscreenActive = false;
    this._fullscreenCheckIntervalId = null;
  }

  attachPenSeekGuard = () => {
    const videoEl = this.player && this.player.video && this.player.video.video;
    if (!videoEl) return;
    if (this._penSeekHandler) videoEl.removeEventListener('seeking', this._penSeekHandler);
    if (this._penTimeHandler) videoEl.removeEventListener('timeupdate', this._penTimeHandler);
    this._penSeekGuardedVideo = videoEl;
    // ペン線が無い時だけ「直前の位置」を更新し続ける（戻し先として使う）
    this._penLockedTime = videoEl.currentTime;
    this._penTimeHandler = () => {
      const { listItemSvg } = this.state;
      if (!(listItemSvg && listItemSvg.length > 0)) {
        this._penLockedTime = videoEl.currentTime;
      }
    };
    videoEl.addEventListener('timeupdate', this._penTimeHandler);
    // シークが起きた瞬間：ペン線があれば位置を元へ引き戻し、modalを出す
    this._penSeekHandler = () => {
      const { listItemSvg } = this.state;
      if (listItemSvg && listItemSvg.length > 0) {
        if (Math.abs(videoEl.currentTime - this._penLockedTime) > 0.05) {
          videoEl.currentTime = this._penLockedTime; // 動かさない（元へ戻す）
        }
        if (!this.state.penConfirmModal) {
          this.setState({ penConfirmModal: {} });
        }
      }
    };
    videoEl.addEventListener('seeking', this._penSeekHandler);
  };

  componentWillUnmount() {
    document.removeEventListener('tm-modal-back-click', this.handleModalBackClick);
    document.removeEventListener(COMMENT_BOX_OPENED_EVENT, this.handleCommentBoxOpened);
    if (this._fullscreenCheckIntervalId) {
      clearInterval(this._fullscreenCheckIntervalId);
      this._fullscreenCheckIntervalId = null;
    }
    if (this._penSeekGuardedVideo && this._penSeekHandler) {
      this._penSeekGuardedVideo.removeEventListener('seeking', this._penSeekHandler);
      if (this._penTimeHandler) this._penSeekGuardedVideo.removeEventListener('timeupdate', this._penTimeHandler);
    }
  }

  handleModalBackClick = (e) => {
    if (!this.props.isOpen) return;

    if (typeof this.closeModal === 'function') {
      this.closeModal();
    } else if (this.props.onClickClose) {
      this.props.onClickClose();
    } else if (this.props.toggle) {
      this.props.toggle();
    }
  };

  componentDidMount() {
    document.addEventListener('tm-modal-back-click', this.handleModalBackClick);
    document.addEventListener(COMMENT_BOX_OPENED_EVENT, this.handleCommentBoxOpened);
    // Fallback for fullscreen implementations that don't always trigger fullscreenchange.
    this._fullscreenCheckIntervalId = setInterval(this.syncControlBarFullscreen, 250);

    if (this.props.currentAnimation.need_conversion) {
      return;
    }

    setTimeout(() => {
      if (this.refs.shortcut) {
        this.refs.shortcut.shortcuts = [];
      }
      if (!this.player || typeof this.player.play !== 'function') return;
      this.player.play();
      this.player.pause();
      this.player.subscribeToStateChange((state, prevState) => {
        // ① 既存：停止→再生を検知
        if (prevState.paused && !state.paused) {
          const { listItemSvg } = this.state;
          if (listItemSvg && listItemSvg.length > 0) {
            this.player.pause();
            this.setState({ penConfirmModal: {} });
          }
        }
        // ② 追加：停止中のシークを検知（◀▶・スキップボタン）
        if (state.paused && state.currentTime !== prevState.currentTime) {
          const { listItemSvg } = this.state;
          if (listItemSvg && listItemSvg.length > 0) {
            this.setState({ penConfirmModal: {} });
          }
        }
      });
      // [2026-06-29] B案: playerが差し替わっても効くよう、video要素のseekingを直接監視（再生中/停止中/5秒すべて拾う）
      this.attachPenSeekGuard();
    }, 500);
    this.reset();
    var width = this.elementRef ? this.elementRef.offsetWidth : 0;
    var height = this.elementRef ? this.elementRef.offsetHeight : 0;
    this.setState({
      canvasWidth: width,
      canvasHeight: height,
      currentCanvasWidth: width,
      currentCanvasHeight: height,
    });
    this.disabledScrollBodyOfVideoMobile()
    // window.addEventListener("resize", this.update);
    // Trigger a custom event that the ERB file can listen for
    const {currentAnimation} = this.props;
    const canOpenModal = currentAnimation?.additional_info?.gdrive_pdf ||( currentAnimation?.additional_info?.gdrive_file && currentAnimation?.additional_info?.gdrive_image == undefined)
    if(canOpenModal && canUpdateGoogleDriveDeleteLogic(this.props.role) && this.props.mainAdmin) {
      const event = new CustomEvent('gdriveFileDeletedModalLoaded', {'detail': currentAnimation?.additional_info?.handle_delete});
      document.dispatchEvent(event);
    } else {
      const event = new CustomEvent('gdriveFileDeletedModalUnLoaded');
      document.dispatchEvent(event);
    }
  }

  getFullscreenElement = () =>
    document.fullscreenElement ||
    document.webkitFullscreenElement ||
    document.mozFullScreenElement ||
    document.msFullscreenElement;

  isVideoReactFullscreenActive = () => {
    // Fallback when browser fullscreen API isn't reliably triggered by video-react.
    const controlBar = document.querySelector('.video-react-control-bar');
    if (!controlBar) return false;

    const root = controlBar.closest('.video-react') || document.querySelector('.video-react');
    if (!root) return false;

    const rect = root.getBoundingClientRect();
    const widthMatches = rect.width >= window.innerWidth - 10;
    const heightMatches = rect.height >= window.innerHeight - 10;
    return widthMatches && heightMatches;
  };

  handleFullscreenChange = () => {
    const controlBar = document.querySelector('.video-react-control-bar');
    if (!controlBar) return;

    const isFullscreenActive =
      !!this.getFullscreenElement() || this.isVideoReactFullscreenActive();

    if (isFullscreenActive) {
      if (this._controlBarTopBeforeFullscreen === null) {
        const computedStyle = window.getComputedStyle(controlBar);
        this._controlBarTopBeforeFullscreen =
          computedStyle.top || controlBar.style.top || '278px';
      }
      if (this._controlBarPositionBeforeFullscreen === null) {
        const computedStyle = window.getComputedStyle(controlBar);
        this._controlBarPositionBeforeFullscreen =
          computedStyle.position || controlBar.style.position || '';
      }

      // Force override of CSS top and use bottom in fullscreen.
      controlBar.style.setProperty('top', 'auto', 'important');
      controlBar.style.setProperty('bottom', '0', 'important');
      controlBar.style.setProperty('left', '0', 'important');
      controlBar.style.setProperty('right', '0', 'important');
      controlBar.style.setProperty('position', 'absolute', 'important');
    } else {
      controlBar.style.bottom = '';
      controlBar.style.left = '';
      controlBar.style.right = '';
      controlBar.style.top = this._controlBarTopBeforeFullscreen ?? '278px';
      controlBar.style.position = this._controlBarPositionBeforeFullscreen ?? '';

      this._controlBarTopBeforeFullscreen = null;
      this._controlBarPositionBeforeFullscreen = null;
    }
  };

  handleCommentBoxOpened = (event) => {
    if (event.detail?.sourceId === this.commentBoxSyncId) return;

    this.setState({
      shouldShowForm: false,
      isMarking: false,
      showPeriodRange: [],
      periodRange: null,
    });
  }

  openMarkerForm = (nextState = {}, callback) => {
    this.props.updateSelectedMarker(null);
    announceCommentBoxOpened(this.commentBoxSyncId);
    this.setState({
      shouldShowForm: true,
      isMarking: false,
      ...nextState,
    }, callback);
  }

  syncControlBarFullscreen = () => {
    // Only adjust on mobile layout.
    if (window.innerWidth > 720) {
      this._isFullscreenActive = false;
      return;
    }

    const isFullscreenActive =
      !!this.getFullscreenElement() || this.isVideoReactFullscreenActive();
    if (isFullscreenActive === this._isFullscreenActive) return;
    this._isFullscreenActive = isFullscreenActive;
    this.handleFullscreenChange();
  };
  componentDidUpdate(prevProps) {
    if (prevProps.isCommentMode && !this.props.isCommentMode) {
      var _bp = document.querySelector('.video-react-big-play-button');
      if (_bp) _bp.style.opacity = '';
    }
    if (this.props.seekTime !== prevProps.seekTime && this.props.seekTime != null) {
      this.player.pause();
      this.seekTo(this.props.seekTime);
      if (this.props.onSeeked) this.props.onSeeked();
    }
  }
  disabledScrollBodyOfVideoMobile() {
    if (window.innerWidth < 768) {
      document.querySelector('body').style.setProperty('overflow' ,'hidden',  'important')
    } else {
      document.querySelector('body').style.setProperty('overflow' ,'auto',  'important')
    }
  }
  update = () => {
    if (this.props.currentAnimation.need_conversion) {
      return;
    }
    let width = this.elementRef ? this.elementRef.offsetWidth : 0;
    let height = this.elementRef ? this.elementRef.offsetHeight : 0;

    if (width !== this.state.canvasWidth) {
      this.setState(
        {
          canvasWidth: width,
          canvasHeight: height,
          listItemSvg: [],
        },
        () => {
          this.ctx = this.canvas.getContext("2d");
          this.ctx.clearRect(
            0,
            0,
            this.state.canvasWidth,
            this.state.canvasHeight
          );
          this.ctx.drawImage(
            this.player.video.video,
            0,
            0,
            this.state.canvasWidth,
            this.state.canvasHeight
          );
        }
      );
    }
  }
  componentWillReceiveProps(newProps) {
    if (newProps.settingDraw != this.state.settingDraw) {
      this.setState({
        settingDraw: newProps.settingDraw,
      });
    }
  }
  mouseRelativeXY(event) {
    return this.relativeXY(event.clientX, event.clientY);
  }
  mouseRelativeXYDraw(event) {
    return this.relativeXY(
      event.nativeEvent.clientX,
      event.nativeEvent.clientY
    );
  }
  touchRelativeXY(event) {
    return this.relativeXY(event.touches[0].clientX, event.touches[0].clientY);
  }
  relativeXY(eventX, eventY) {
    const playerRect = findDOMNode(this.player).getBoundingClientRect();
    return [
      (eventX - playerRect.left) / playerRect.width * 10000,
      (eventY - playerRect.top) / playerRect.height * 10000,
    ];
  }
  startMark = ({ x, y }) => {
    if (!this.props.isCommentMode) return;
    var _bp = document.querySelector('.video-react-big-play-button'); if (_bp) _bp.style.opacity = '0';
    let currentTime = 0, duration = 0;
    if (this.player && typeof this.player.pause === 'function') {
      try {
        this.player.pause();
        const state = this.player.getState();
        if (state && state.player) {
          currentTime = state.player.currentTime;
          duration = state.player.duration;
        }
      } catch(e) {}
    }

    if (this.props.onVideoClicked) {
      this.props.onVideoClicked(currentTime, duration);
    }

    this.setState({
      isMarking: true,
      shouldShowForm: false,
      startX: x,
      startY: y,
      currentX: x,
      currentY: y,
      currentWidth: 0,
      currentHeight: 0,
      currentShape: 'point',
      currentTime,
    });
  }
  moveMark = ({ x, y }) => {
    const { isMarking, startX, startY } = this.state;
    if (!isMarking) return;
    const { max, abs } = Math;
    const distance = max(abs(x - startX), abs(y - startY));
    distance > 10 && startX < x && startY < y
      ? this.setState({
        currentWidth: max(x - startX, 0),
        currentHeight: max(y - startY, 0),
        currentShape: 'rect',
      })
      : this.setState({
        currentWidth: 0,
        currentHeight: 0,
        currentShape: 'point',
      })
  }
  // ダブルクリック判定用
  _lastTapTime = 0;
  _DOUBLE_TAP_MS = 300;

  _showTapHint = () => {
    let toast = document.getElementById('tm-mode-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'tm-mode-toast';
      toast.style.cssText = [
        'position:fixed', 'top:0', 'left:0',
        'transform:translate(-50%,-50%)',
        'background:rgba(0,0,0,0.75)', 'color:#fff',
        'padding:10px 18px', 'border-radius:8px',
        'font-size:13px', 'pointer-events:none',
        'z-index:2147483647', 'white-space:nowrap',
        'opacity:0', 'transition:opacity 0.2s'
      ].join(';');
      document.body.appendChild(toast);
    }
    toast.textContent = 'ダブルクリックするとコメントモードに切り替わります';
    const _getMediaRect = () => {
      try {
        const root = findDOMNode(this.player);
        const el = root ? root.querySelector('.video-react-video') || root : null;
        return el ? el.getBoundingClientRect() : null;
      } catch(e) { return null; }
    };
    const rect = _getMediaRect();
    if (rect) {
      toast.style.top = (rect.top + rect.height / 2) + 'px';
      toast.style.left = (rect.left + rect.width / 2) + 'px';
    } else {
      toast.style.top = '50%';
      toast.style.left = '50%';
    }
    toast.style.opacity = '1';
    clearTimeout(this._toastTimer);
    this._toastTimer = setTimeout(() => { toast.style.opacity = '0'; }, 1000);
  };

  _showModeToast = (text) => {
    let toast = document.getElementById('tm-mode-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'tm-mode-toast';
      toast.style.cssText = [
        'position:fixed', 'top:0', 'left:0',
        'transform:translate(-50%,-50%)',
        'background:rgba(0,0,0,0.75)', 'color:#fff',
        'padding:10px 18px', 'border-radius:8px',
        'font-size:13px', 'pointer-events:none',
        'z-index:2147483647', 'white-space:nowrap',
        'opacity:0', 'transition:opacity 0.2s'
      ].join(';');
      document.body.appendChild(toast);
    }
    toast.textContent = text;
    const _getMediaRect = () => {
      try {
        const root = findDOMNode(this.player);
        const el = root ? root.querySelector('.video-react-video') || root : null;
        return el ? el.getBoundingClientRect() : null;
      } catch(e) { return null; }
    };
    const rect = _getMediaRect();
    if (rect) {
      toast.style.top = (rect.top + rect.height / 2) + 'px';
      toast.style.left = (rect.left + rect.width / 2) + 'px';
    } else {
      toast.style.top = '50%';
      toast.style.left = '50%';
    }
    toast.style.opacity = '1';
    clearTimeout(this._toastTimer);
    this._toastTimer = setTimeout(() => { toast.style.opacity = '0'; }, 1000);
  };

  _showCommentMode = () => {
    this._doubleActivatedAt = Date.now();
    this._doubleConfirmed = true;
    this._justEnteredCommentMode = true;
    if (!this._preventScrollFn) {
      this._preventScrollFn = (e) => { e.preventDefault(); };
    }
    document.addEventListener('touchmove', this._preventScrollFn, { passive: false });
    if (this.props.onEnterCommentMode) this.props.onEnterCommentMode();
  };

  _hideCommentMode = () => {
    if (this._preventScrollFn) {
      document.removeEventListener('touchmove', this._preventScrollFn, { passive: false });
    }
    this._doubleActivatedAt = 0;
    this._doubleConfirmed = false;
    if (this.props.onExitCommentMode) this.props.onExitCommentMode();
  };

  endMark = (event) => {
    if (!this.props.isCommentMode) return;
    if (this._justEnteredCommentMode) {
      this._justEnteredCommentMode = false;
      return;
    }
    if(!this.state.isMarking) return
    event.stopPropagation();
    event.preventDefault();
    const { currentAnimationId, animationSelectCurrent, toggleShowModalInputInfoOfNotLogin, isInputInfoMationOfNotLogin } = this.props;
    this.setState({ isMarking: false });
    if (currentAnimationId === animationSelectCurrent) {
      if (gon.is_login || isInputInfoMationOfNotLogin) {
        if (this.props.isOpenNouislider && !this.state.periodRange) {
          Popup.confirm(`範囲の指定が1ヶ所のみです。<br>範囲指定を終了し、コメントを入力しますか？`, {
            confirmText: 'OK',
            success: () => {
              this.props.closeNouislider();
              this.openMarkerForm({ showPeriodRange: [] });
            },
            cancel: () => {
              this.setState({ shouldShowForm: false, showPeriodRange: [], isMarking: false });
            }
          });
        } else {
          this.openMarkerForm({ showPeriodRange: [] });
        }
      } else {
        toggleShowModalInputInfoOfNotLogin();
      }
      this.disableMmount();
    }
  };
  openPeriod = (periodRange) => {
    if (this.state.startX === 0 &&
      this.state.startY === 0 &&
      this.state.currentX === 0 &&
      this.state.currentY === 0) {
      this.openMarkerForm({
        periodRange: periodRange,
        startX: 1000,
        startY: 1000,
        currentX: 1000,
        currentY: 1000
      });
    } else {
      this.openMarkerForm({
        periodRange: periodRange,
        showPeriodRange: [],
      });
    }
  }
  pauseVideo = () => {
    this.player.pause();
  }
  getStyleOnMode = ()=>{
    const {onMode} = this.props
    let style={}
    onMode ? style ={
      top:0,
      bottom:"unset"
    } : style ={
      top:"unset"
    }
    return style
  }
  disableMmount(e = "") {
    var menus = document.querySelectorAll(".show-mark-shape");
    menus = [].slice.call(menus);
    menus.forEach(function (element) {
      element.style.pointerEvents = e;
    });
  }

  isTouchInsidePlayer = (event) => {
    if (!event || !event.touches || !event.touches[0] || !this.player) return true;
    const playerRect = findDOMNode(this.player).getBoundingClientRect();
    const touchX = event.touches[0].clientX;
    const touchY = event.touches[0].clientY;
    return (
      touchX >= playerRect.left &&
      touchX <= playerRect.right && 
      touchY >= playerRect.top &&
      touchY <= playerRect.bottom
    );
  }

  overlay() {
    return () => (
      <div
        className="overlay akapon"
        style={this.getStyleOnMode()}
        onMouseDown={(event) => {
          if (this._touchHandled) return;
          event.stopPropagation();
          event.preventDefault();
          const now = Date.now();
          const delta = now - this._lastTapTime;
          this._lastTapTime = now;
          if (delta < this._DOUBLE_TAP_MS && delta > 0) {
            this._lastTapTime = 0;
            if (!this.props.isCommentMode) {
              this._showCommentMode();
              this._showModeToast('コメントモードに切り替わりました');
            }
            return;
          }
          if (!this.props.isCommentMode) {
            this._showTapHint();
            return;
          }
          this.disableMmount("none");
          const [x, y] = this.mouseRelativeXY(event);
          this.startMark({ x, y });
        }}
        onDoubleClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
        }}
        onTouchStart={(event) => {
          this._touchHandled = true;
          setTimeout(() => { this._touchHandled = false; }, 500);
          // 閲覧モード時、再生ボタン上のタップは透過させる
          if (!this.props.isCommentMode) {
            var _t = event.touches[0];
            var _bp2 = document.querySelector('.video-react-big-play-button');
            if (_bp2) {
              var _r2 = _bp2.getBoundingClientRect();
              if (_t.clientX >= _r2.left && _t.clientX <= _r2.right &&
                  _t.clientY >= _r2.top && _t.clientY <= _r2.bottom) {
                return;
              }
            }
          }
          event.stopPropagation();
          event.preventDefault();
          this.setOption();
          const now = Date.now();
          const delta = now - this._lastTapTime;
          this._lastTapTime = now;
          if (delta < this._DOUBLE_TAP_MS && delta > 0) {
            this._lastTapTime = 0;
            if (!this.props.isCommentMode) {
              this._showCommentMode();
              this._showModeToast('コメントモードに切り替わりました');
            }
            return;
          }
          if (!this.props.isCommentMode) {
            this._showTapHint();
            return;
          }
          var _touch = event.touches[0];
          var _bp = document.querySelector('.video-react-big-play-button');
          if (_bp) {
            var _bpRect = _bp.getBoundingClientRect();
            if (_touch.clientX >= _bpRect.left && _touch.clientX <= _bpRect.right &&
                _touch.clientY >= _bpRect.top && _touch.clientY <= _bpRect.bottom) {
              this.player.play();
              return;
            }
          }
          const [x, y] = this.touchRelativeXY(event);
          this.startMark({ x, y });
        }}
        onMouseMove={(event) => {
          event.stopPropagation();
          event.preventDefault();
          const [x, y] = this.mouseRelativeXY(event);
          this.moveMark({ x, y });
        }}
        onTouchMove={(event) => {
          event.stopPropagation();
          event.preventDefault();
          if (!this.isTouchInsidePlayer(event)) {
            this.endMark(event);
            return;
          }
          const [x, y] = this.touchRelativeXY(event);
          this.moveMark({ x, y });
        }}
        onMouseUp={this.endMark}
        onTouchEnd={this.endMark}
        onTouchCancel={this.endMark}
        onClick={this.setOption}
      ></div>
    );
  }
  onSubmitMarkerForm = (body, files, editorSetting) => {
    const { onSubmitMarkerForm } = this.props;
    const {
      currentX: x,
      currentY: y,
      currentWidth: width,
      currentHeight: height,
      currentShape: shape,
      currentTime: time,
      periodRange
    } = this.state;
    return onSubmitMarkerForm({
      body,
      x,
      y,
      width,
      height,
      shape,
      time,
      files,
      editorSetting,
      periodRange
    }).then(() =>
      this.setState({
        shouldShowForm: false,
        periodRange: null,
        startX: 0,
        startY: 0,
        currentX: 0,
        currentY: 0,
        currentShape: 'point',
        listItemSvg: [],
        isDel: false,
        isDraw: false,
        // [2026-06-29] 動画: 保存後もペンモード維持。ペン時のみ前面化（撮影は終了済みで競合なし）
        zIndexSvg: this.props.isShowDrawComponent ? showSVGIndex : hideSVGIndex,
      })
    );
  };
  onCloseMarkerForm = () => {
    this.setState(
      {
        shouldShowForm: false,
      },
      () => {
        this.ctx = this.canvas.getContext("2d");
        this.ctx.clearRect(
          0,
          0,
          this.state.canvasWidth,
          this.state.canvasHeight
        );
        this.ctx.drawImage(
          this.player.video.video,
          0,
          0,
          this.state.canvasWidth,
          this.state.canvasHeight
        );
      }
    );
  };
  activeMarkers() {
    // [2026-06-29] ペンモード中はマーカーNo（番号付き赤丸）を画面に出さない仕様
    if (this.props.isShowDrawComponent) { return []; }
    const { markers, selectedMarker } = this.props;
    const { currentTime } = this.state;
    // ★FIX(20260405): markerが選択されている場合はそのmarkerのみ表示
    // クリック時に近傍±1秒のmarkerが全て表示されるバグを修正
    // selectedMarker は詳細BOX閉じ/削除/返信時に null にリセットされる
    if (selectedMarker && selectedMarker.id != null) {
      const targetId = selectedMarker.isComment
        ? selectedMarker.id : selectedMarker.parent_id;
      return markers.filter(_ => _.id === targetId);
    }
    // 未選択時（動画再生中など）：従来通り±1秒のフィルター
    return markers.filter(_ =>
      (currentTime - 1 <= _.seek_time && _.seek_time <= currentTime + 1) ||
        (_.period_end > 0 && _.seek_time <= currentTime && currentTime - 1 <= _.period_end)
    );
  }
  onTimeUpdate() {
    const { player: { currentTime } } = this.player.getState();
    this.setState({ currentTime });
    // ペンなし・ペンモードOFF の時はcanvasを動画フレームで更新
    const { listItemSvg, isDraw, isDel } = this.state;
    if (!isDraw && !isDel && (!listItemSvg || listItemSvg.length === 0)) {
      if (this.canvas && this.player && this.player.video && this.player.video.video) {
        const ctx = this.canvas.getContext("2d");
        // [2026-06-29] 縦長動画を横長に潰さない。動画比率で中央に焼く（黒帯維持）
        const { nativeVideoWidth, nativeVideoHeight, canvasWidth, canvasHeight } = this.state;
        ctx.clearRect(0, 0, canvasWidth, canvasHeight);
        if (nativeVideoWidth > 0 && nativeVideoHeight > 0) {
          const containerRatio = canvasWidth / canvasHeight;
          const videoRatio = nativeVideoWidth / nativeVideoHeight;
          let dx = 0, dy = 0, dw = canvasWidth, dh = canvasHeight;
          if (videoRatio < containerRatio) {
            dw = Math.round(canvasHeight * videoRatio);
            dx = Math.round((canvasWidth - dw) / 2);
          } else {
            dh = Math.round(canvasWidth / videoRatio);
            dy = Math.round((canvasHeight - dh) / 2);
          }
          ctx.drawImage(this.player.video.video, dx, dy, dw, dh);
        } else {
          ctx.drawImage(this.player.video.video, 0, 0, canvasWidth, canvasHeight);
        }
      }
    }
  }
  seekTo(time) {
    this.player.seek(time);
  }
  onClickMarkersToggler = () => {
    const currentIndex = markersVisibilities.indexOf(this.state.markersVisibility);
    const nextIndex = (currentIndex + 1) < markersVisibilities.length ? currentIndex + 1 : 0;
    this.setState({ markersVisibility: markersVisibilities[nextIndex] });
  }
  refCallback = element => {
    const { getHeightAnimaiton } =this.props
    if (element && getHeightAnimaiton) {
      this.elementRef = element;
      getHeightAnimaiton(element.getBoundingClientRect().height);
    }
  };

  showSeekBar(){
    const { mobile } = this.props
    if(mobile) return

    document.querySelector('.video-react-control-bar').style.display = "flex";
    if (!this.props.isCommentMode) {
      document.querySelector('.video-react-big-play-button').style.opacity = "1";
    }
    this.setState({ isShowControlBar: true });
  }

  hintSeekBar(){
    const { mobile } = this.props
    if(mobile) return

    document.querySelector('.video-react-control-bar').style.display = 'none';
    document.querySelector('.video-react-big-play-button').style.opacity = '0.01';
    this.setState({ isShowControlBar: false });
  }

  showSeekBarMobile(){
    const { mobile } = this.props
    if(!mobile)
      return
    var element = document.querySelector('.video-react-paused');
    var playButton = document.querySelector('.video-react-control-bar');
    if (element && playButton){
      this.showSeekBar();
    } else if (playButton){
      playButton.style.display = 'none';
    }
  }

  // [2026-06-29] ❹ ペン切替時に残るテキストの仮マーカーを片付ける（reset丸ごとは呼ばない=副作用なし）
  clearTempMarker() {
    this.setState({
      isMarking: false,
      shouldShowForm: false,
      currentX: 0, currentY: 0,
      currentWidth: 0, currentHeight: 0,
      currentShape: 'point',
      startX: 0, startY: 0,
    });
  }

  draw(e) {
    this.player.video.pause();
    this.setState(
      {
        isDel: false,
        shouldShowForm: false,
        // isLargeScreen: false,
      },
      () => {
        this.setState(
          {
            canvasHeight: this.state.currentCanvasHeight,
            canvasWidth: this.state.currentCanvasWidth,
          },
          () => {
            if (this.canvas) {
              const context = this.canvas.getContext("2d");
              const { nativeVideoWidth, nativeVideoHeight, canvasWidth, canvasHeight } = this.state;
              context.clearRect(0, 0, canvasWidth, canvasHeight);
              if (nativeVideoWidth > 0 && nativeVideoHeight > 0) {
                const containerRatio = canvasWidth / canvasHeight;
                const videoRatio = nativeVideoWidth / nativeVideoHeight;
                let dx = 0, dy = 0, dw = canvasWidth, dh = canvasHeight;
                if (videoRatio < containerRatio) {
                  dw = Math.round(canvasHeight * videoRatio);
                  dx = Math.round((canvasWidth - dw) / 2);
                } else {
                  dh = Math.round(canvasWidth / videoRatio);
                  dy = Math.round((canvasHeight - dh) / 2);
                }
                context.drawImage(this.player.video.video, dx, dy, dw, dh);
              } else {
                context.drawImage(this.player.video.video, 0, 0, canvasWidth, canvasHeight);
              }
            }
          }
        );
      }
    );

    this.setState({
      zIndexSvg: showSVGIndex,
    });
  }

  eraser() {
    this.setState({
      isDel: true,
    });
  }

  capture(e) {
    if (this.elementRef) this.elementRef.style["overflow"] = 'unset';
    this.player.video.pause();
    const currentTime = this.player.video
      ? this.player.video.props.player.currentTime
      : 0;
    const fileDrawName = `draw-video-image-${moment.now()}`;
    const files = [];
    var svgURL = new XMLSerializer().serializeToString(this.svg);
    const canvas = this.canvas;
    const context = canvas.getContext("2d");
    var img = new Image();
    img.onload = () => {
      if (this.canvas) this.canvas.style.zIndex = '0';
      // [2026-06-29] 撮影の正しい順序: まずcanvasを消し→動画フレームを比率維持で焼き→その上にペンの絵を重ねる
      // これで「同じ尺2回目で動画が写らずNull」「縦長動画が横長に潰れる」を同時に解消
      const { nativeVideoWidth, nativeVideoHeight, canvasWidth, canvasHeight } = this.state;
      const drawRatio = (src) => {
        if (nativeVideoWidth > 0 && nativeVideoHeight > 0) {
          const containerRatio = canvasWidth / canvasHeight;
          const videoRatio = nativeVideoWidth / nativeVideoHeight;
          let dx = 0, dy = 0, dw = canvasWidth, dh = canvasHeight;
          if (videoRatio < containerRatio) {
            dw = Math.round(canvasHeight * videoRatio);
            dx = Math.round((canvasWidth - dw) / 2);
          } else {
            dh = Math.round(canvasWidth / videoRatio);
            dy = Math.round((canvasHeight - dh) / 2);
          }
          context.drawImage(src, dx, dy, dw, dh);
        } else {
          context.drawImage(src, 0, 0, canvasWidth, canvasHeight);
        }
      };
      context.clearRect(0, 0, canvasWidth, canvasHeight);
      // ① 動画フレームを焼く（同じ尺でも確実に写す）
      if (this.player && this.player.video && this.player.video.video) {
        drawRatio(this.player.video.video);
      }
      // ② その上にペンの絵を重ねる
      drawRatio(img);
      var imageSrc = this.canvas.toDataURL();
      const { listItemSvg } = this.state;
      if (listItemSvg.length === 0) {
        return;
      } else {
        const { listItemSvg } = this.state;
        const target = this.state.currentItemSvg || listItemSvg[0]
           const arr = target.capturePoint.split(",");
        const convertArr = arr[1].trim().split(" ");
        const markPointCaptureY = Number(convertArr[0]);
        const markPointCaptureX = Number(convertArr[1]);
        this.setState({
          currentX: markPointCaptureX,
          currentY: markPointCaptureY,
        });
      }
      this.urltoFile(imageSrc, fileDrawName, "image/png").then((file) => {
        files.push(file);
        this.openMarkerForm(
          {
            currentShape: "draw",
            currentTime: currentTime,
          },
          () => {
            this.makerForm.onFileChange(null, files);
          }
        );
        // [2026-06-29] 動画: 撮影(toDataURL)が完全に終わった後に前面化。撮影用canvasはもう使わない＝白画像が出ない
        if (this.props.isShowDrawComponent) {
          this.setState({ zIndexSvg: showSVGIndex });
        }
      });
    };
    img.src = "data:image/svg+xml; charset=utf8, " + encodeURIComponent(svgURL);
  }

  urltoFile(url, filename, mimeType) {
    return fetch(url)
      .then(function (res) {
        return res.arrayBuffer();
      })
      .then(function (buf) {
        return new File([buf], filename, { type: mimeType });
      });
  }

  deleteAllDraw() {
    this.setState({
      isDel: false,
      listItemSvg: [],
      shouldShowForm: false,
      zIndexSvg: hideSVGIndex,
      isLargeScreen: true,
    });
  }

  reset(isDelete) {
    if (this.props.currentAnimation.need_conversion) {
      return;
    }

    if(!isDelete){
      this.setState({
        isDraw: false,
        isDel: false,
        listItemSvg: [],
        shouldShowForm: false,
        zIndexSvg: hideSVGIndex,
        isLargeScreen: true,
      });
    } else {
      this.setState({
        isDraw: false,
        isDel: false,
        shouldShowForm: false,
        isLargeScreen: true,
      },
      () => {
        this.setState(
          {
            canvasHeight: this.state.currentCanvasHeight,
            canvasWidth: this.state.currentCanvasWidth,
          },
          () => {
            if (this.canvas) {
              const context = this.canvas.getContext("2d");
              // [2026-06-29] 縦長動画でも正しい比率で描く（横長に潰さない）。855行側と同じ処理に統一
              const { nativeVideoWidth, nativeVideoHeight, canvasWidth, canvasHeight } = this.state;
              context.clearRect(0, 0, canvasWidth, canvasHeight);
              if (nativeVideoWidth > 0 && nativeVideoHeight > 0) {
                const containerRatio = canvasWidth / canvasHeight;
                const videoRatio = nativeVideoWidth / nativeVideoHeight;
                let dx = 0, dy = 0, dw = canvasWidth, dh = canvasHeight;
                if (videoRatio < containerRatio) {
                  dw = Math.round(canvasHeight * videoRatio);
                  dx = Math.round((canvasWidth - dw) / 2);
                } else {
                  dh = Math.round(canvasWidth / videoRatio);
                  dy = Math.round((canvasHeight - dh) / 2);
                }
                context.drawImage(this.player.video.video, dx, dy, dw, dh);
              } else {
                context.drawImage(this.player.video.video, 0, 0, canvasWidth, canvasHeight);
              }
            }
          }
        );
      });
    }
    this.ctx = this.canvas.getContext("2d");
    this.ctx.clearRect(0, 0, this.state.canvasWidth, this.state.canvasHeight);
  }

  disableScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

    window.onscroll = function() {
      window.scrollTo(scrollLeft, scrollTop);
    };
  }

  enableScroll() {
    window.onscroll = function() {};
  }

  onMouseDown(event, isMobile) {
    if (this.player) this.player.video.pause();
    if (isMobile) {
      if (this.elementRef) this.elementRef.style["overflow"] = "hidden";
      document.body.style.overflow = "hidden";
    }
    if (!this.state.isDraw && !this.state.isDel) {
      this.setState({
        isDraw: true,
      });
      var newItem = {
        id: Date.now(),
        points: "",
        color: this.state.settingDraw.color,
        lineWidth: this.state.settingDraw.lineWidth,
        capturePoint: "",
      };
      if (!isMobile) {
        var [x, y] = this.mouseRelativeXYDraw(event);
        newItem.capturePoint = ` , ${y} ${x}`;
      } else {
        var [x, y] = this.touchRelativeXY(event);
        newItem.capturePoint = ` , ${y} ${x}`;
        this.disableScroll();
      }
      this.setState({
        listItemSvg: [...this.state.listItemSvg, newItem],
        currentItemSvg: newItem,
      });
    }
  }

  onMouseMove = (event, isMobile) => {
    if (this.state.isDraw) {
      let heightTopToAnimation = fetchHeightHeader()
      var item = document.getElementById(this.state.currentItemSvg.id);
      var points = item.getAttribute("points");
      var capturePoint = item.getAttribute("capturePoint");
      if (isMobile) {
        let xh = event.nativeEvent.layerX;
        let yh = event.nativeEvent.layerY
        xh = (xh * this.state.currentCanvasWidth / this.player.video.video.offsetWidth).toFixed()
        yh = (yh *  this.state.currentCanvasHeight / this.player.video.video.offsetHeight).toFixed()
        points += ` ${xh}, ${yh}`;
        var [x, y] = this.mouseRelativeXYDraw(event);
        capturePoint += ` ${x}, ${y}`;
      } else {
        var [x, y] = this.touchRelativeXY(event);
        capturePoint += ` ${x}, ${y}`;
        const videoEl = this.player.video.video;
        const videoRect = videoEl.getBoundingClientRect();
        let xh = ((event.touches[0].clientX - videoRect.left) / videoRect.width * this.state.currentCanvasWidth).toFixed();
        let yh = ((event.touches[0].clientY - videoRect.top) / videoRect.height * this.state.currentCanvasHeight).toFixed();
        points += ` ${xh}, ${yh}`;
      }
      let itemInList = this.state.listItemSvg.find(
        (obj) => obj.id === this.state.currentItemSvg.id
      );
      if (itemInList) {
        itemInList.points = points;
        itemInList.capturePoint = capturePoint;
        itemInList.currentPoints = points;
        itemInList.currentCapturePoint = capturePoint;
      }
      item.setAttribute("points", points);
      item.setAttribute("capturePoint", capturePoint);
    }
  };

  onMouseUp() {
    document.body.style.overflow = "auto";
    const styleOverflow = this.state.shouldShowForm ? 'unset' : 'auto';
    if (this.elementRef) this.elementRef.style["overflow"] = styleOverflow;
    if (this.state.isDraw) {
      this.setState({
        isDraw: false,
      });
      this.enableScroll()
    }
  }

  onItemMouseOver(event) {
    const { mobile } = this.props;
    const polyWidht = event.target.getBBox().width;
    const polyHeight = event.target.getBBox().height;
    if (!this.state.isDraw && this.state.isDel) {
      if (!mobile) {
        event.target.setAttribute("stroke-dasharray", "5 5");
      }
      if (polyWidht <= 35 && polyHeight <= 35) {
        let id = event.target.getAttribute("id");
        let newLists = this.state.listItemSvg.filter((obj) => obj.id != id);
        this.setState({
          listItemSvg: newLists,
        });
      }
    }
  }

  onItemMouseLeave(event) {
    if (!this.state.isDraw && this.state.isDel) {
      event.target.setAttribute("stroke-dasharray", "");
    }
  }

  onDelete(event) {
    if (!this.state.isDraw && this.state.isDel) {
      let id = event.target.getAttribute("id");
      let newLists = this.state.listItemSvg.filter((obj) => obj.id != id);
      this.setState({
        listItemSvg: newLists,
      });
    }
  }

  setOption = () => {
    const { currentAnimationId, animationSelectCurrent } = this.props;
    if (currentAnimationId !== animationSelectCurrent) {
      this.fetchCurrentAnimation()
    }
  }

  handleOutsideClick = (e) => {
    if (!this.nodeModalVersion.contains(e.target)) {
      this.setOption();
    }
  };

  fetchCurrentAnimation = () => {
    const { setCurrentAnimation } = this.props;
    this.setState({ isShowModalNotCompareVersion: false }, () => setCurrentAnimation())
  }

  handleLoadedMetadata = (e) => {
    const duration = e.currentTarget.duration;
    const videoEl = e.currentTarget;
    this.setState({
      totalDuration: duration,
      nativeVideoWidth: videoEl.videoWidth || 0,
      nativeVideoHeight: videoEl.videoHeight || 0,
    });
  };

  checkPenBeforeAction = (onConfirm) => {
    const { listItemSvg } = this.state;
    if (!listItemSvg || listItemSvg.length === 0) {
      onConfirm();
      return;
    }
    this.setState({ penConfirmModal: {} });
  };

  penConfirmSave = () => {
    this.setState({ penConfirmModal: null });
    this.capture();
  };

  penConfirmDiscard = () => {
    this.setState({
      listItemSvg: [],
      isDel: false,
      isDraw: false,
      shouldShowForm: false,
      penConfirmModal: null,
    });
  };

  penConfirmCancel = () => {
    this.setState({ penConfirmModal: null });
  };

  closeAnimationMarkerForm = () => {
    this.setState({
      shouldShowForm: false,
      periodRange: null,
      startX: 0,
      startY: 0,
      currentX: 0,
      currentY: 0,
      currentShape: 'point',
      showPeriodRange: [],
    })
    if (this.canvas) {
      const ctx = this.canvas.getContext("2d");
      ctx.clearRect(0, 0, this.state.canvasWidth, this.state.canvasHeight);
      this.canvas.style.zIndex = '';
    }
    if (this.props.isOpenNouislider) {
      this.props.updateIsOpenNouislider()
    }
  }

  setPeriodRangeWhenClickTimeLine = (periodRange, comment_number) => {
    this.setState({ showPeriodRange: periodRange, commentNumber: comment_number })
  }

  setShowPeriodRangeFromProp = memoize(
    (showPeriodRangeProp) => {
      this.setState({ showPeriodRange: showPeriodRangeProp })
    }
  );

  toggle = bool => name => {
    this.setState({ [`shouldShow${name}`]: bool });
  }
  open = this.toggle(true)
  close = this.toggle(false)
  fadeInLoader = () => {
    $('.preloader').fadeIn();
    setInterval(() => {
      $('.preloader').css('opacity', 0.2)
    }, 200);
  }
  startLoading = loadingText => {
    this.fadeInLoader();
    this.setState({ loadingText, isLoading: true });
  }

  endLoading = () => {
    this.setState({ loadingText: null, isLoading: false });
    $('.preloader').fadeOut();
    clearInterval(this.percentConvert);
    this.percentConvert = null;
  }
  render() {
    const { src, downloadUrl, currentUserId, animationMarkerCreationPath, markers, markerCreatable,
            onClickDownloadButton, mobile, onMarkerUpdationCalled, updateMakerShow, role, owner, user, scaleImage,
            currentAnimation, animationSelectCurrent, currentAnimationId, onMarkerDeletionCalled, reloadMarkers, apiClient, duration,
            deleteReplyMarker, avatarUrl, isShowDrawComponent, isOpenNouislider, updateIsOpenNouislider, showPeriodRangeProp, project,
            is_project_owner_member
          } = this.props;
    const { currentX, currentY, currentWidth, currentHeight, currentShape, shouldShowForm, currentTime, markersVisibility, isMarking,
            isLargeScreen, isShowControlBar, isShowModalNotCompareVersion, showPeriodRange, shouldShowGdriveFileDeletedModal, commentNumber } = this.state;
    this.setShowPeriodRangeFromProp(showPeriodRangeProp);
    const Overlay = this.overlayComponent;
    const encodeName = src.split('/').pop()
    const decodeName = decodeURI(decodeURI(encodeName));
    let src2 = `${src.replace(encodeName, decodeName)}?v=${timestamp}`
    return (
      <div className={mobile ? 'wrap-animation-preview-for-mobile-video' : ''}>
        { currentAnimation.need_conversion ? (
          <div
            id="wrap-center"
            style={{minHeight:"94%"}}
            className={`wrap-animation-preview ${mobile ? 'wrap-animation-preview-image-for-mobile' : ''} ${getStyleOfSize(scaleImage,"wrap")}`}>
            <div
              className={`animation-preview ${getStyleOfSize(scaleImage)}` }
              id="animation-preview"
              style={{width:`${scaleImage}%`}}
              ref={(ref) => (this.wrap = ref)}
            >
              <div
                className="animation-image"
                id="animation-image"
              >
                <div className='project__image' id="project__image">
                  <img
                    crossOrigin="anonymous" onLoad={this.onImageLoad} src={imgConvertingThumbnail}
                    alt='logo' id="img"
                  />
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div id="wrap-center"
          className={`wrap-animation-preview ${getStyleOfSize(scaleImage,"wrap")}`}
          style={{minHeight:"100%", position:'relative'}}
          >
            <div
                id="animation-player"
                style={{ width: `${scaleImage}%` }}
                className={classnames("animation-player", { marking: isMarking },{'d-table mt-auto mb-auto':scaleImage && scaleImage <100})}
                ref={this.refCallback}
                onMouseEnter={this.showSeekBar}
                onMouseLeave={this.hintSeekBar}
              >
                <canvas
                  id="canvas"
                  ref={(ref) => (this.canvas = ref)}
                  width={`${this.state.canvasWidth}px`}
                  height={`${this.state.canvasHeight}px`}
                  style={Object.assign({}, styles.canvas, {
                    zIndex: this.state.zIndexSvg,
                    display: this.state.zIndexSvg === 0 ? 'block' : 'none',
                  })}
                ></canvas>

                <div
                  ref={(ref) => (this.drawSvg = ref)}
                  className="draw-svg"
                  id="draw-svg"
                  style={{
                    zIndex: this.state.zIndexSvg,
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    touchAction: 'none',
                  }}
                  onMouseDown={(event) => this.onMouseDown(event, false)}
                  onMouseMove={(event) => this.onMouseMove(event, true)}
                  onMouseUp={(event) => this.onMouseUp(event, false)}
                  onMouseLeave={() => this.onMouseUp()}
                  onTouchStart={(event) => this.onMouseDown(event, true)}
                  onTouchEnd={() => this.onMouseUp()}
                  onTouchMove={(event) => this.onMouseMove(event, false)}
                >
                  <svg
                    viewBox={`0 0 ${this.state.canvasWidth} ${this.state.canvasHeight}`}
                    className={
                      this.state.isDel
                        ? "cursor_eraser"
                        : `cursor_pencil_${this.state.settingDraw.color}`
                    }
                    width={`${this.state.canvasWidth}px`}
                    height={`${this.state.canvasHeight}px`}
                    ref={(ref) => (this.svg = ref)}
                    style={Object.assign({}, styles.canvas, {
                      zIndex: this.state.zIndexSvg,
                    })}
                  >
                    {this.state.listItemSvg.map((item, i) => (
                      <PolyLineSvg
                        id={item.id}
                        points={item.points}
                        color={item.color}
                        lineWidth={item.lineWidth}
                        onItemMouseOver={(event) => this.onItemMouseOver(event)}
                        onItemMouseLeave={(event) => this.onItemMouseLeave(event)}
                        onDelete={(event) => this.onDelete(event)}
                      />
                    ))}
                  </svg>
                </div>
              <Player
                playsInline={true}
                src={src2} aspectRatio={isLargeScreen ? "16:9" : "auto"} ref={ref => this.player = ref}
                onTimeUpdate={this.onTimeUpdate.bind(this)}
                id="video"
                isFullscreen={true}
                crossOrigin="anonymous"
                onLoadedMetadata={this.handleLoadedMetadata}
                muted={true}
              >
                {
                  <Overlay order="2" />
                }
                {
                  isMarking && <MarkShape x={currentX} y={currentY} width={currentWidth} height={currentHeight} shape={currentShape} isCommentPeriod={isOpenNouislider} />
                }
                <BigPlayButton position="center" />
                <ControlBar autoHide={false}>
                  <SettingButton order={7} rates={[0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2]}
                                isShowControlBar={isShowControlBar} />
                  <VolumeMenuButton vertical order={2} />
                </ControlBar>
                <AnimationMarkersInPlayer
                  markers={this.activeMarkers()}
                  visibility={markersVisibility}
                  order={11}
                  onMarkerUpdationCalled={onMarkerUpdationCalled}
                  image={this.player}
                  updateMakerShow={updateMakerShow}
                />
                {
                  shouldShowForm && (
                    <AnimationMarkerForm
                      ref={(ref) => (this.makerForm = ref)}
                      x={currentX} y={currentY} width={currentWidth} height={currentHeight} shape={currentShape}
                      time={currentTime} onSubmit={this.onSubmitMarkerForm}
                      onClickClose={this.closeAnimationMarkerForm} order={12}
                      mobile={mobile} shouldShowForm={shouldShowForm} currentUserId={currentUserId}
                      role={role} owner={owner} user={user}
                      scaleImage={scaleImage}
                      is_embed_video={false}
                      is_show_draw_component={true}
                      onlyBUAForCommentPeriod={isOpenNouislider}
                    />
                  )
                }

                <Shortcut ref="shortcut" />
              </Player>

            </div>
            <CommentDetail
              selectedMarker={this.props.selectedMarker}
              updateSelectedMarker={this.props.updateSelectedMarker}
              onMarkerUpdationCalled={onMarkerUpdationCalled}
              currentUserId={currentUserId}
              owner={owner}
              role={role}
              animationSelectCurrent={animationSelectCurrent}
              currentAnimationId={currentAnimationId}
              onMarkerDeletionCalled={onMarkerDeletionCalled}
              reloadMarkers={reloadMarkers}
              apiClient={apiClient}
              markers={markers}
              deleteReplyMarker={deleteReplyMarker}
              avatarUrl={avatarUrl}
              shouldShowForm={shouldShowForm}
              is_project_owner_member={is_project_owner_member}
            />
            <div ref={nodeModalVersion => { this.nodeModalVersion = nodeModalVersion }}>
              <Modal isOpen={isShowModalNotCompareVersion}
                  className="animation-uploader-modal modal-dialog-centered modal-not-compare-version"
                  size="md" >
                <ModalBody>
                  <div className='text-center text-black'>
                    <div className='font-size-17px font-weight-bold text-black text-center'>
                      <span>メディア画面とコメントツールの </span>
                      <span>バージョンが異なる為、赤入れできません。</span>
                    </div>
                    <Button className="btn btn-danger my-2 button-set-current-animation" onClick={this.fetchCurrentAnimation}>
                      表示中のメディア画面を赤入れする
                    </Button>
                    <span>
                      ※クリックするとコメントツールのバージョンが{currentAnimation.final ? '最終稿' : currentAnimation.title}に変わります。
                    </span>
                  </div>
                </ModalBody>
              </Modal>
            </div>
            {this.state.penConfirmModal && (
              <div data-pen-confirm-modal="true" style={{
                position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                background: 'rgba(0,0,0,0.6)', zIndex: 2147483647,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <div style={{
                  background: '#1a1a1a', color: '#fff', borderRadius: '16px',
                  boxShadow: '0 20px 48px rgba(0,0,0,.6)',
                  padding: '32px 28px 24px', maxWidth: '320px',
                  width: 'calc(100vw - 48px)', textAlign: 'center', fontFamily: 'sans-serif',
                }}>
                  <p style={{ fontSize: '15px', lineHeight: 1.7, margin: '0 0 24px', fontWeight: 500, color: '#fff' }}>
                    ペン入力中です。<br />このまま閉じてもよろしいですか？
                  </p>
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexDirection: 'column' }}>
                    <button onClick={this.penConfirmSave} style={{
                      padding: '10px 0', border: 'none', borderRadius: '10px',
                      background: '#2563eb', color: '#fff',
                      fontSize: '14px', fontWeight: 700, cursor: 'pointer',
                      boxShadow: '0 4px 12px rgba(37,99,235,.4)',
                    }}>保存して閉じる</button>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button onClick={this.penConfirmDiscard} style={{
                        flex: 1, padding: '10px 0', border: 'none', borderRadius: '10px',
                        background: '#e60c11', color: '#fff',
                        fontSize: '14px', fontWeight: 700, cursor: 'pointer',
                        boxShadow: '0 4px 12px rgba(230,12,17,.4)',
                      }}>破棄して閉じる</button>
                      <button onClick={this.penConfirmCancel} style={{
                        flex: 1, padding: '10px 0', border: 'none', borderRadius: '10px',
                        background: 'rgba(255,255,255,.12)', color: '#fff',
                        fontSize: '14px', fontWeight: 700, cursor: 'pointer',
                      }}>キャンセル</button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      { !currentAnimation.need_conversion && (
      <div className="comment-timeline-container">
        <AnimationMarkerLine markers={markers}
          duration={this.state.totalDuration}
          player={this.player}
          isEmbedVideo={false}
          order={10}
          isShowDrawComponent={isShowDrawComponent}
          setPeriodRangeWhenClickTimeLine={this.setPeriodRangeWhenClickTimeLine}
          onMarkerLineClicked={this.props.onMarkerLineClicked}
        />
        <AnimationMarkerPeriod
          duration={this.state.totalDuration}
          player={this.player}
          isEmbedVideo={false}
          openPeriodCommentBox={this.openPeriod}
          isOpenNouislider={isOpenNouislider}
          updateIsOpenNouislider={updateIsOpenNouislider}
          showPeriodRange={showPeriodRange}
          commentNumber={commentNumber}
          onMarkerPeriodChanged={this.props.onMarkerPeriodChanged}
          commentPeriodContainer={this.props.commentPeriodContainer}
        ></AnimationMarkerPeriod>
        <GdriveFileDeletedModal
          isOpen={shouldShowGdriveFileDeletedModal}
          onClickClose={this.close.bind(this, 'GdriveFileDeletedModal')}
          apiClient={apiClient}
          currentAnimationId={currentAnimationId}
          currentAnimation={currentAnimation}
          onStart={this.startLoading.bind(this, 'Now Processing...')}
          endLoading={this.endLoading}
          type='video'
          project={project}
          applyAllGdrive={this.props.applyAllGdrive}
          canUseApplyAll={this.props.canUseApplyAll}
          defaultGdriveLogic={this.props.defaultGdriveLogic}
        />
        <NavLink href="#!" className='hidden d-none' id="gdriveFileDeletedModal" onClick={this.open.bind(this, 'GdriveFileDeletedModal')}>
        </NavLink>
      </div>
      )}
    </div>
    )
  }
}
